import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { OFFERS } from "@/data/mockData";
import { agentsApi, templatesApi } from "@/lib/api";
import { PageHeader, EditorialButton, Pill } from "@/lib/editorial";
import { ChevronLeft, ChevronDown, ChevronUp, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function AgentTemplates() {
  const { id } = useParams();
  const { data: agent } = useQuery({ queryKey: ["agent", id], queryFn: () => agentsApi.get(id), enabled: !!id });
  const { data: templateData } = useQuery({ queryKey: ["templates", id], queryFn: () => templatesApi.get(id), enabled: !!id });

  const [overrides, setOverrides] = useState({});
  const [expanded, setExpanded] = useState(new Set());
  const [savedBanner, setSavedBanner] = useState(false);

  useEffect(() => {
    if (templateData?.overrides) setOverrides(templateData.overrides);
  }, [templateData]);

  const defaults = templateData?.defaults ?? {};

  const saveMut = useMutation({
    mutationFn: () => templatesApi.save(id, overrides),
    onSuccess: () => {
      setSavedBanner(true);
      toast("Template overrides saved.");
      setTimeout(() => setSavedBanner(false), 4000);
    },
  });

  const groupA = Object.keys(defaults).filter((k) => !k.includes("_offer_"));
  const groupB = Object.keys(defaults).filter((k) => k.includes("_offer_"));

  function toggleExpand(k) {
    const next = new Set(expanded);
    next.has(k) ? next.delete(k) : next.add(k);
    setExpanded(next);
  }

  function save() { saveMut.mutate(); }

  return (
    <div className="px-8 lg:px-14 py-12">
      <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.22em] text-[var(--muted-light)] mb-6">
        <Link to="/admin/agents" className="hover:text-[var(--ink)] flex items-center gap-1">
          <ChevronLeft className="w-3.5 h-3.5" /> Agents
        </Link>
        <span>/</span>
        <Link to={`/admin/agents/${id}`} className="hover:text-[var(--ink)]">{agent?.name}</Link>
        <span>/</span>
        <span className="text-[var(--ink)]">Template overrides</span>
      </div>

      <PageHeader
        eyebrow="Templates"
        tape="Message"
        rest="overrides"
        sub="Non-empty fields will be saved as overrides for this agent. A blank field means use the global default."
        testId="templates-title"
      />

      {savedBanner && (
        <div
          className="mb-8 inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-50 border border-emerald-300 text-emerald-700 text-[12px]"
          data-testid="templates-save-banner"
        >
          <CheckCircle2 className="w-4 h-4" /> Overrides saved successfully.
        </div>
      )}

      <TemplateGroup
        title="Audience / Segment templates"
        keys={groupA}
        defaults={defaults}
        overrides={overrides}
        setOverrides={setOverrides}
        expanded={expanded}
        toggleExpand={toggleExpand}
      />

      <div className="mt-16">
        <TemplateGroup
          title="Offer-specific templates"
          keys={groupB}
          defaults={defaults}
          overrides={overrides}
          setOverrides={setOverrides}
          expanded={expanded}
          toggleExpand={toggleExpand}
          offerGroup
        />
      </div>

      <div className="mt-12 flex items-center gap-3">
        <EditorialButton onClick={save} data-testid="templates-save">
          Save overrides
        </EditorialButton>
        <Link
          to={`/admin/agents/${id}`}
          className="text-[12px] uppercase tracking-[0.22em] text-[var(--muted-light)] hover:text-[var(--ink)]"
        >
          Cancel
        </Link>
      </div>
    </div>
  );
}

function TemplateGroup({ title, keys, defaults, overrides, setOverrides, expanded, toggleExpand, offerGroup }) {
  return (
    <section>
      <div className="flex items-end justify-between border-b border-[var(--rule-light)] pb-3 mb-6">
        <h2 className="font-display text-[26px] uppercase leading-none tracking-tight">{title}</h2>
        <span className="text-[11px] font-mono text-[var(--muted-light)]">{keys.length} keys</span>
      </div>

      {offerGroup ? (
        <div className="space-y-10">
          {OFFERS.map((o) => (
            <div key={o.id}>
              <div className="text-[10px] uppercase tracking-[0.24em] text-[var(--muted-light)] mb-3">
                Offer {o.id} · {o.name}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[`cold_template_offer_${o.id}`, `followup_template_offer_${o.id}`].map((k) => (
                  <TemplateCard
                    key={k}
                    k={k}
                    defaults={defaults}
                    overrides={overrides}
                    setOverrides={setOverrides}
                    expanded={expanded}
                    toggleExpand={toggleExpand}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {keys.map((k) => (
            <TemplateCard
              key={k}
              k={k}
              defaults={defaults}
              overrides={overrides}
              setOverrides={setOverrides}
              expanded={expanded}
              toggleExpand={toggleExpand}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function TemplateCard({ k, defaults, overrides, setOverrides, expanded, toggleExpand }) {
  const isOpen = expanded.has(k);
  const hasOverride = (overrides[k] ?? "").trim().length > 0;
  const def = defaults[k] ?? "";
  return (
    <div className="border border-[var(--rule-light)] bg-white p-5" data-testid={`template-card-${k}`}>
      <div className="flex items-center justify-between gap-3 mb-3">
        <span className="font-mono text-[11px] text-[var(--ink)]">{k}</span>
        {hasOverride ? (
          <Pill color="blue">override active</Pill>
        ) : (
          <Pill>using default</Pill>
        )}
      </div>
      <button
        onClick={() => toggleExpand(k)}
        className="flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] text-[var(--muted-light)] hover:text-[var(--ink)]"
      >
        {isOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        Show default
      </button>
      {isOpen && (
        <pre className="mt-3 bg-[var(--paper-2)] border border-[var(--rule-light)] p-3 text-[12px] whitespace-pre-wrap font-mono">
{def}
        </pre>
      )}
      <textarea
        value={overrides[k] ?? ""}
        onChange={(e) => setOverrides({ ...overrides, [k]: e.target.value })}
        rows={4}
        placeholder={def}
        className="mt-3 w-full border border-[var(--rule-light)] focus:border-[var(--blue)] outline-none p-3 text-[13px] leading-relaxed"
      />
    </div>
  );
}
